import styled from 'styled-components';

import { useAppDispatch, useAppSelector } from '../../redux/store-hooks';

import { Box, CircularProgress, Stack, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  actingUserAppSelectors,
  updateMemberInfoSectionPreference,
} from 'redux/slices/actingUserSlice';
import {
  getMember,
  membersAppSelectors,
  selectSelectedMember,
} from 'redux/slices/memberSlice';
import { selectedMemberSelectors } from 'redux/slices/selectedMemberSlice';
import ConsentInformationCard from './profile-cards/ConsentInformationCard';
import InsuranceInformationCard from './profile-cards/InsuranceInformationCard';
import MemberPortraitCard from './profile-cards/MemberPortraitCard';
import PersonalInformationCard from './profile-cards/PersonalInformationCard';
import ProviderInformationCard from './profile-cards/ProviderInformationCard';
import CommunicationCard from './profile-cards/CommunicationCard';

const LoadingPageStyled = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  .MuiCircularProgress-root {
    position: absolute;
    left: 50%;
    top: 50%;
  }
`;

const MemberInfo = () => {
  const theme = useTheme();
  const [hasRequested, setHasRequested] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const userPreferences = actingUserAppSelectors.useSelectUserPreferences();
  const memberSliceStatus = membersAppSelectors.useSelectSliceStatus();
  const selectedMember = useAppSelector(selectSelectedMember);

  const phoneNumbers = selectedMember?.phoneNumbers;
  const addresses = selectedMember?.addresses;
  const standardMemberAttributes = selectedMember?.standardMemberAttributes;

  const memberId = selectedMemberSelectors.useSelectSelectedMemberId();
  const member = membersAppSelectors.useSelectMemberById(memberId);
  const isLoading = memberSliceStatus === 'loading';

  useEffect(() => {
    if (!member && !isLoading && !hasRequested) {
      // Admins can see all members, as well as Client Auditors,
      // Try to get member if member is not already loaded
      setHasRequested(true);
      dispatch(getMember({ params: { id: memberId } }));
    }
  }, [dispatch, hasRequested, isLoading, member, memberId]);

  function getDefaultClosed(section: string) {
    return userPreferences?.memberProfile?.perMemberPreferences?.[memberId]?.[
      section
    ]?.collapsed;
  }

  function toggleSection(section: string) {
    void dispatch(
      updateMemberInfoSectionPreference({
        memberId,
        section,
        preference: {
          collapsed:
            !userPreferences?.memberProfile?.perMemberPreferences?.[memberId]?.[
              section
            ]?.collapsed,
        },
      })
    );
  }

  if (isLoading || !member) {
    return (
      <LoadingPageStyled>
        <CircularProgress />
      </LoadingPageStyled>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        padding: '12px 12px 0',
        overflowY: 'scroll',
        boxSizing: 'border-box',
        backgroundColor: theme.palette.surfaces.neutralLighter,
      }}
    >
      <Stack spacing={1} padding={0}>
        <ConsentInformationCard
          member={member}
          defaultClosed={getDefaultClosed('consents')}
          onToggle={() => toggleSection('consents')}
        />
        <PersonalInformationCard
          member={member}
          attributes={standardMemberAttributes}
          defaultClosed={getDefaultClosed('personalInformation')}
          onToggle={() => toggleSection('personalInformation')}
        />
        <MemberPortraitCard
          member={member}
          defaultClosed={getDefaultClosed('memberPortrait')}
          onToggle={() => toggleSection('memberPortrait')}
        />
        <CommunicationCard
          member={member}
          addresses={addresses}
          phoneNumbers={phoneNumbers}
          defaultClosed={getDefaultClosed('memberCommunication')}
          onToggle={() => toggleSection('memberCommunication')}
        />
        <ProviderInformationCard
          attributes={standardMemberAttributes}
          defaultClosed={getDefaultClosed('providerInformation')}
          onToggle={() => toggleSection('providerInformation')}
        />
        <InsuranceInformationCard
          member={member}
          attributes={standardMemberAttributes}
          defaultClosed={getDefaultClosed('insuranceInformation')}
          onToggle={() => toggleSection('insuranceInformation')}
        />
      </Stack>
    </Box>
  );
};

export default MemberInfo;
